import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Card from '@mui/material/Card';
import CriticalAlerts from '../alerts/CriticalAlerts';
import './index.css';
import TeamPerformance from '../alerts/TeamPerformance';
import BotAlerts from '../alerts/BotAlerts';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';

const StyledCard = styled(Card)`
  border-radius: 8px;
  background: var(--Dark-Dark-11-White, #FFF);
  box-shadow: 0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 3px 5px 0px rgba(9, 30, 66, 0.20);
  padding-top: 8px;
  min-height: calc(100vh - 200px);
`;

const StyledTab = styled(Tab)`
  text-transform: capitalize;
  color: var(--Dark-Dark-03, #546077);
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;

  &.Mui-selected {
    color: var(--Dark-Dark-01, #0E1E40);
    font-family: Inter;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px;
  }
`;

function CustomTabPanel(props) {
  const {
    children, value, index, p = 4,
  } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && (
        <Box sx={{ p }}>
          {children}
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
  p: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf([null]),
  ]),
};

function AlertLabel({ label, count }) {
  return (
    <span>
      {label}
      {count > 0 && (
        <span style={{
          background: '#d32f2f',
          color: '#fff',
          borderRadius: '8px',
          padding: '2px 8px',
          fontSize: '12px',
          marginLeft: '2px',
          marginBottom: '5px',
          verticalAlign: 'middle',
          display: 'inline-block',
          minWidth: '20px',
          textAlign: 'center',
        }}
        >
          {count}
        </span>
      )}
    </span>
  );
}

AlertLabel.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
};

function TabularDashboard() {
  const { activeWorkspace } = useContext(UserContext);
  const [value, setValue] = useState(0);
  const [alertsCount, setAlertsCount] = useState({
    critical: 0,
    teamPerformance: 0,
    hrBot: 0,
    itBot: 0,
    infosecBot: 0,
  });

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const getAlertsCount = async () => {
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getPendingAlertsCount(activeWorkspace.id),
    });

    if (response.status === 200 && Array.isArray(response.data.counts)) {
      const counts = response.data.counts.reduce((acc, curr) => {
        if (curr.category === 'Critical') acc.critical = Number(curr.count) || 0;
        if (curr.category === 'HR') acc.hrBot = Number(curr.count) || 0;
        if (curr.category === 'IT') acc.itBot = Number(curr.count) || 0;
        if (curr.category === 'Infosec') acc.infosecBot = Number(curr.count) || 0;
        return acc;
      }, {
        critical: 0,
        teamPerformance: 0,
        hrBot: 0,
        itBot: 0,
        infosecBot: 0,
      });
      setAlertsCount(counts);
    }
  };

  useEffect(() => {
    getAlertsCount();
  }, []);

  return (
    <StyledCard>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="settings tab"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: 'var(--Blue-Blue-01, #213D69)',
            },
          }}
        >
          <StyledTab label={(
            <AlertLabel label="Critical Alerts" count={alertsCount.critical} />
          )}
          />
          <StyledTab label="Team Performance" />
          <StyledTab label={(
            <AlertLabel label="HR Bot Alerts" count={alertsCount.hrBot} />
          )}
          />
          <StyledTab label={(
            <AlertLabel label="IT Bot Alerts" count={alertsCount.itBot} />
          )}
          />
          <StyledTab label={(
            <AlertLabel label="Infosec Bot Alerts" count={alertsCount.infosecBot} />
          )}
          />
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0} p={0}>
        <CriticalAlerts />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1} p={0}>
        <TeamPerformance />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2} p={0}>
        <BotAlerts category="HR" />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={3}>
        <BotAlerts category="IT" />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={4}>
        <BotAlerts category="Infosec" />
      </CustomTabPanel>
    </StyledCard>
  );
}

export default TabularDashboard;
